/**
 * Zustand store for admin role override/testing
 *
 * Allows system administrators to temporarily test the application as different user roles
 * without logging out or affecting the database. The override is client-side only and
 * persists across page reloads via localStorage.
 *
 * Safety mechanisms:
 * - Only admins can activate (enforced in usePermissions hook)
 * - Auto-disables when switching FBOs
 * - Preserves system admin escape hatch (ACCESS_SUPER_ADMIN always true)
 *
 * @example
 * const { isActive, overriddenRole, startRoleOverride, stopRoleOverride } = useRoleOverride();
 *
 * // Start testing as operator
 * startRoleOverride('operator', activeFBO.id);
 *
 * // Exit role testing
 * stopRoleOverride();
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type OverridableRole = "fbo_admin" | "manager" | "operator" | "csr" | null;

interface RoleOverrideState {
  /** The role being tested (null = no override active) */
  overriddenRole: OverridableRole;

  /** Whether override is currently active */
  isActive: boolean;

  /** The FBO ID this override applies to (used to detect FBO switching) */
  fboId: string | null;

  /** Start testing as a different role */
  startRoleOverride: (role: OverridableRole, fboId: string) => void;

  /** Stop role testing and return to normal admin permissions */
  stopRoleOverride: () => void;
}

export const useRoleOverride = create(
  persist<RoleOverrideState>(
    (set) => ({
      overriddenRole: null,
      isActive: false,
      fboId: null,

      startRoleOverride: (role, fboId) => {
        set({
          overriddenRole: role,
          isActive: true,
          fboId,
        });
      },

      stopRoleOverride: () => {
        set({
          overriddenRole: null,
          isActive: false,
          fboId: null,
        });
      },
    }),
    {
      name: "role-override-storage",
    }
  )
);
